import jwtDecode from 'jwt-decode'
import { useCallback, useEffect, useRef, useState } from 'react'

export const APPLE_REG_CONFIRM_SSO_ERROR_NOTIFICATION_KEY =
  'apple-reg-confirm-sso-error'

const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || ''
const APPLE_REDIRECT_URI = (
  process.env.REACT_APP_APPLE_REG_CONFIRM_REDIRECT_URI || ''
).trim()
const APPLE_SCRIPT_ID = 'apple-sign-in'
const APPLE_SCRIPT_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js'

let appleScriptPromise

const loadAppleScript = () => {
  if (window.AppleID?.auth) {
    return Promise.resolve()
  }

  if (appleScriptPromise) {
    return appleScriptPromise
  }

  appleScriptPromise = new Promise((resolve, reject) => {
    const existingScript =
      document.getElementById(APPLE_SCRIPT_ID) ||
      document.querySelector(`script[src^="${APPLE_SCRIPT_SRC}"]`)

    if (existingScript) {
      existingScript.addEventListener('load', resolve, { once: true })
      existingScript.addEventListener('error', reject, { once: true })
      return
    }

    const script = document.createElement('script')
    script.id = APPLE_SCRIPT_ID
    script.src = APPLE_SCRIPT_SRC
    script.type = 'text/javascript'
    script.async = true
    script.defer = true
    script.onload = resolve
    script.onerror = reject
    document.head.appendChild(script)
  })

  return appleScriptPromise
}

const useAppleRegConfirmSso = ({ enabled, onEmail, onError }) => {
  const onEmailRef = useRef(onEmail)
  const onErrorRef = useRef(onError)
  const [isReady, setIsReady] = useState(false)

  onEmailRef.current = onEmail
  onErrorRef.current = onError

  const handleAuthorization = useCallback(response => {
    const idToken = response?.authorization?.id_token

    if (!idToken) {
      onErrorRef.current('Apple Sign-In did not return credentials.')
      return
    }

    try {
      const email = jwtDecode(idToken)?.email

      if (!email) {
        onErrorRef.current('Apple Sign-In did not return an email.')
        return
      }

      onEmailRef.current(email)
    } catch {
      onErrorRef.current('Apple Sign-In failed. Please try again.')
    }
  }, [])

  useEffect(() => {
    if (!enabled || !APPLE_CLIENT_ID || !APPLE_REDIRECT_URI) {
      setIsReady(false)
      return undefined
    }

    const handleSuccess = event => {
      handleAuthorization(event.detail)
    }
    const handleFailure = event => {
      if (event.detail?.error === 'popup_closed_by_user') {
        return
      }

      onErrorRef.current('Apple Sign-In failed. Please try again.')
    }
    let isMounted = true

    loadAppleScript()
      .then(() => {
        if (!isMounted || !window.AppleID?.auth) {
          return
        }

        window.AppleID.auth.init({
          clientId: APPLE_CLIENT_ID,
          scope: 'email',
          redirectURI: APPLE_REDIRECT_URI,
          usePopup: true,
        })

        document.addEventListener('AppleIDSignInOnSuccess', handleSuccess)
        document.addEventListener('AppleIDSignInOnFailure', handleFailure)
        setIsReady(true)
      })
      .catch(() => {
        appleScriptPromise = undefined

        if (isMounted) {
          setIsReady(false)
          onErrorRef.current(
            'Apple Sign-In is unavailable. Please try again later.',
          )
        }
      })

    return () => {
      isMounted = false
      document.removeEventListener('AppleIDSignInOnSuccess', handleSuccess)
      document.removeEventListener('AppleIDSignInOnFailure', handleFailure)
    }
  }, [enabled, handleAuthorization])

  const trigger = useCallback(() => {
    if (isReady) {
      return true
    }

    onErrorRef.current('Apple Sign-In is not ready. Please try again.')
    return false
  }, [isReady])

  return {
    isReady,
    trigger,
  }
}

export default useAppleRegConfirmSso
